import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Clock, ArrowRight, Search } from "lucide-react";

const CATEGORIES = ["All", "Hormones", "Hair", "Cycle", "Wellness", "PCOS"];

export default function Learn() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("All");
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/articles").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const term = q.toLowerCase();
        return items.filter((t) => {
            const cat = (t.category || t.tag || "").toLowerCase();
            const matchCat = category === "All" || cat.includes(category.toLowerCase());
            const matchQ = !term || t.title.toLowerCase().includes(term) || (t.excerpt || "").toLowerCase().includes(term);
            return matchCat && matchQ;
        });
    }, [items, category, q]);

    const featured = items[0];
    const rest = filtered.filter((x) => !featured || x.id !== featured.id);

    return (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14" data-testid="learn-page">
            <div className="text-center mb-10 sm:mb-12">
                <p className="eyebrow">Knowledge, expertly curated</p>
                <h1 className="h-display text-[40px] sm:text-[58px] lg:text-[72px] mt-3">Learn</h1>
                <p className="text-[var(--body)] max-w-xl mx-auto mt-3 text-[14px] sm:text-[15px]">
                    Evidence-reviewed insights on hormones, hair, cycles, and the science behind every formulation.
                </p>
            </div>

            {loading ? (
                <div className="py-20 text-center text-[var(--body)]">Loading…</div>
            ) : items.length === 0 ? (
                <div className="card p-12 text-center">
                    <h3 className="font-serif text-2xl text-[var(--ink)] mb-2">Articles coming soon</h3>
                    <p className="text-[var(--body)] mb-5">Our editorial board is publishing the first set of articles.</p>
                </div>
            ) : (
                <>
                    {/* Featured */}
                    {featured && category === "All" && !q && (
                        <Link to={`/learn/${featured.id}`} className="card-glass mb-10 grid md:grid-cols-2 overflow-hidden group" data-testid="featured-article">
                            <div className="aspect-[4/3] md:aspect-auto bg-[var(--bg-alt)] overflow-hidden">
                                {featured.cover_image ? (
                                    <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                ) : (
                                    <div className="w-full h-full grid place-items-center text-7xl">📖</div>
                                )}
                            </div>
                            <div className="p-6 sm:p-10 flex flex-col justify-center">
                                <div className="overline mb-3 flex items-center gap-2">
                                    <span>{featured.category || featured.tag || "Featured"}</span>
                                    {featured.read_time && (<><span className="text-[var(--line)]">·</span><span className="inline-flex items-center gap-1"><Clock size={11} /> {featured.read_time} min read</span></>)}
                                </div>
                                <h2 className="font-serif text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.05] text-[var(--ink)] mb-4">{featured.title}</h2>
                                <p className="text-[14px] text-[var(--body)] leading-relaxed mb-5 line-clamp-3">{featured.excerpt}</p>
                                <span className="text-[13px] uppercase tracking-[0.18em] text-[var(--taupe)] inline-flex items-center gap-1">
                                    Read article <ArrowRight size={14} />
                                </span>
                            </div>
                        </Link>
                    )}

                    {/* Filters */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-8">
                        <div className="flex flex-wrap gap-2 overflow-x-auto -mx-1 px-1" data-testid="learn-categories">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCategory(c)}
                                    className={`btn btn-sm whitespace-nowrap ${category === c ? "btn-primary" : "btn-outline"}`}
                                    data-testid={`learn-cat-${c.toLowerCase()}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <div className="relative max-w-[260px]">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--body)] pointer-events-none" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles…"
                                className="field"
                                style={{ paddingLeft: 36 }}
                                data-testid="learn-search"
                            />
                        </div>
                    </div>

                    {/* Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                        {(category === "All" && !q ? rest : filtered).map((t) => (
                            <Link to={`/learn/${t.id}`} key={t.id} className="card-glass overflow-hidden group flex flex-col hover-float" data-testid={`article-${t.id}`}>
                                <div className="aspect-[5/3] bg-gradient-to-br from-[var(--bg-alt)] to-[var(--rose)]/40 overflow-hidden">
                                    {t.cover_image ? (
                                        <img src={t.cover_image} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <div className="w-full h-full grid place-items-center text-5xl">📖</div>
                                    )}
                                </div>
                                <div className="p-5 flex flex-col flex-1">
                                    <div className="overline mb-2">{t.category || t.tag || "Wellness"}</div>
                                    <h3 className="font-serif text-[20px] sm:text-[22px] text-[var(--ink)] leading-snug mb-3 group-hover:text-[var(--taupe-deep)] transition">{t.title}</h3>
                                    <p className="text-[13px] text-[var(--body)] leading-relaxed mb-4 line-clamp-3">{t.excerpt}</p>
                                    <span className="text-[12px] uppercase tracking-[0.18em] text-[var(--taupe)] inline-flex items-center gap-1 mt-auto self-start group-hover:gap-2 transition-all">
                                        Read <ArrowRight size={12} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
